import React, { useContext, useEffect, useState } from 'react';

import {
    Box,
    LinearProgress,
    Paper
} from '@mui/material';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';

import { Alerta } from '../../components/alerta';
import CrmMetricCard from '../../components/crmMetricCard';
import DayRangePicker from '../../components/dayRangePicker';
import MultiSelectFilter from '../../components/multiSelectFilter';
import DadosUsuario from '../../utils/context';
import {
    formatDecimal,
    formatInteger
} from '../../utils/numberUtils';
import {
    compactCurrency,
    formatCurrency
} from '../../utils/stringFormat';
import {
    buildDashboardDateRange,
    buildDashboardFilters,
    buildGoalProjectionText,
    buildMarketingCostViewModel,
    buildDashboardViewModel,
    buildSellerGoalViewModel,
    CRM_DASHBOARD_MIN_DATE,
    EMPTY_DASHBOARD_DATA,
    EMPTY_MARKETING_DATA,
    EMPTY_PENDING_SIGNATURES,
    fetchCrmDashboard,
    fetchMarketingData,
    fetchPendingSignatures
} from './crmDashboardData';
import { PendingSignaturePanel, SellerGoalCard } from './crmDashboardGoal';
import { CrmDashboardMobile } from './crmDashboardMobile';
import { DashboardPanels } from './crmDashboardPanels';
import {
    FASE_OPTIONS,
    VENDEDOR_OPTIONS,
    buildGroupedOriginOptions,
    buildGroupedStatusOptions,
    getSellerIdByEmail
} from './crmShared';
import '../css/crmDashboard.css';

const metricTone = [
    { accent: 'var(--setta-amber)', helper: 'var(--ok)', icon: <TimelineRoundedIcon sx={{ fontSize: 18 }} /> },
    { accent: 'var(--app-text)', helper: 'var(--ok)', icon: <AssessmentRoundedIcon sx={{ fontSize: 18 }} /> },
    { accent: 'var(--app-muted)', helper: 'var(--app-muted)', icon: <InsightsRoundedIcon sx={{ fontSize: 18 }} /> },
    { accent: 'var(--chart-3)', helper: 'var(--erro)', icon: <PercentRoundedIcon sx={{ fontSize: 18 }} /> },
    { accent: 'var(--chart-4)', helper: 'var(--ok)', icon: <PaidRoundedIcon sx={{ fontSize: 18 }} /> }
];

const allFilterOption = 'all';
const sellerFilterOptions = [
    { value: allFilterOption, label: 'Todos os Representantes' },
    ...VENDEDOR_OPTIONS.map((option) => ({ value: option.id, label: option.label }))
];
const statusFilterOptions = [
    { value: allFilterOption, label: 'Todos os Status' },
    ...buildGroupedStatusOptions()
];
const phaseFilterOptions = [
    { value: allFilterOption, label: 'Todas as Fases' },
    ...FASE_OPTIONS.map((option) => ({ value: option.id, label: option.label }))
];
const originFilterOptions = [
    { value: allFilterOption, label: 'Todas as Origens' },
    ...buildGroupedOriginOptions()
];

const areSameFilterValues = (currentValues, nextValues) => (
    currentValues.length === nextValues.length &&
    currentValues.every((id, index) => id === nextValues[index])
);

const normalizeMultiSelectValues = (values) => (values.includes(allFilterOption) ? [] : values);

const DashboardHero = () => (
    <div className='crm-dashboard-hero'>
        <h1 className='crm-dashboard-title'>SETTA CRM</h1>
        <p className='crm-dashboard-body crm-dashboard-hero-copy'>
            Dashboard Executivo - Métricas em tempo real.
        </p>
    </div>
);

const DashboardFilters = ({
    canEditSellerFilter,
    dateRange,
    draftFilters,
    onDateRangeApply,
    onDateRangeReset,
    onFilterClose,
    onMultiSelectDraftChange
}) => (
    <Paper className='crm-dashboard-filter-shell'>
        <div className='crm-dashboard-filter-row'>
            <MultiSelectFilter
                className='crm-dashboard-filter-control'
                labelId='vendedor-filter-label'
                label='Vendedor'
                value={draftFilters.vendedor_id}
                options={sellerFilterOptions}
                allOptionValue={allFilterOption}
                disabled={!canEditSellerFilter}
                onChange={(values) => onMultiSelectDraftChange('vendedor_id', values)}
                onClose={() => onFilterClose('vendedor_id')}
            />

            <MultiSelectFilter
                className='crm-dashboard-filter-control'
                labelId='status-filter-label'
                label='Status'
                value={draftFilters.status_id}
                options={statusFilterOptions}
                allOptionValue={allFilterOption}
                onChange={(values) => onMultiSelectDraftChange('status_id', values)}
                onClose={() => onFilterClose('status_id')}
            />

            <MultiSelectFilter
                className='crm-dashboard-filter-control'
                labelId='fase-filter-label'
                label='Fase'
                value={draftFilters.fase_id}
                options={phaseFilterOptions}
                allOptionValue={allFilterOption}
                onChange={(values) => onMultiSelectDraftChange('fase_id', values)}
                onClose={() => onFilterClose('fase_id')}
            />

            <MultiSelectFilter
                className='crm-dashboard-filter-control'
                labelId='origem-filter-label'
                label='Origem'
                value={draftFilters.origem_id}
                options={originFilterOptions}
                allOptionValue={allFilterOption}
                onChange={(values) => onMultiSelectDraftChange('origem_id', values)}
                onClose={() => onFilterClose('origem_id')}
            />

            <div className='crm-dashboard-range-field'>
                <span className='crm-dashboard-range-label'>Período</span>
                <DayRangePicker
                    ariaLabel='Filtrar dashboard por período'
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    minDate={CRM_DASHBOARD_MIN_DATE}
                    maxDate={new Date()}
                    onApply={onDateRangeApply}
                    onClear={onDateRangeReset}
                />
            </div>
        </div>
    </Paper>
);

const DashboardSummary = ({
    dateRange,
    goalTarget,
    marketingCost,
    totalLeads,
    wonCount,
    wonKwh
}) => {
    const averageWonTicket = wonCount > 0 ? wonKwh / wonCount : 0;
    const conversionRate = totalLeads > 0 ? (wonCount / totalLeads) * 100 : 0;
    const formattedWonCount = formatInteger(wonCount);
    const formattedTotalLeads = formatInteger(totalLeads);
    const wonLeadText = `${formattedWonCount} ${wonCount === 1 ? 'conquistado' : 'conquistados'}`;
    const wonGoalProjectionText = buildGoalProjectionText({
        dateRange,
        wonTicket: wonKwh,
        target: goalTarget
    });
    const metrics = [
        {
            label: 'Total de Leads',
            value: formattedTotalLeads,
            helperText: `${wonLeadText} no período`
        },
        {
            label: 'Conquistado',
            value: formatCurrency(wonKwh),
            helperText: wonGoalProjectionText
        },
        {
            label: 'Ticket Médio',
            value: formatCurrency(averageWonTicket),
            helperText: `${wonLeadText} no cálculo`
        },
        {
            label: 'Taxa de Conv.',
            value: `${formatDecimal(conversionRate)}%`,
            helperText: `${formattedWonCount} de ${formattedTotalLeads} leads`
        },
        {
            label: 'Custo de Marketing',
            value: compactCurrency(marketingCost.totalCost),
            helperText: `${formatCurrency(marketingCost.costPerWonKwh)} por kWh conquistado`
        }
    ];

    return (
        <div className='ui-kpi-grid'>
            {metrics.map((item, index) => <CrmMetricCard key={item.label} {...item} {...metricTone[index]} />)}
        </div>
    );
};

const CrmDashboard = ({ isMobile = false }) => {
    const { autorizacoes = [], email = '' } = useContext(DadosUsuario);
    const canEditSellerFilter = autorizacoes.includes('Adm') || autorizacoes.includes('MKT');
    const lockedSellerId = canEditSellerFilter ? null : getSellerIdByEmail(email);
    const [dateRange, setDateRange] = useState(buildDashboardDateRange);
    const [filters, setFilters] = useState(() => {
        const initialFilters = buildDashboardFilters();

        return {
            ...initialFilters,
            vendedor_id: lockedSellerId === null ? initialFilters.vendedor_id : [lockedSellerId]
        };
    });
    const [draftFilters, setDraftFilters] = useState(filters);
    const [dashboardData, setDashboardData] = useState(EMPTY_DASHBOARD_DATA);
    const [marketingData, setMarketingData] = useState(EMPTY_MARKETING_DATA);
    const [pendingSignatures, setPendingSignatures] = useState(EMPTY_PENDING_SIGNATURES);
    const [isLoadingPending, setIsLoadingPending] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [statusAlerta, setStatusAlerta] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);

    const viewModel = buildDashboardViewModel(dashboardData);
    const marketingCost = buildMarketingCostViewModel({
        marketingData,
        selectedOriginIds: filters.origem_id,
        wonKwh: viewModel.wonKwh
    });
    const goal = buildSellerGoalViewModel({
        dateRange,
        sellerIds: filters.vendedor_id,
        wonTicket: viewModel.wonKwh,
        pendingTicket: pendingSignatures.totalTicket
    });

    useEffect(() => {
        if (canEditSellerFilter) return;
        if (lockedSellerId === null) {
            setMessage(`Usuário CRM sem vendedor mapeado para o e-mail ${email}.`);
            setStatusAlerta('error');
            setOpenAlerta(true);
            return;
        }

        const lockedSellerFilter = [lockedSellerId];
        setFilters((prev) => (
            areSameFilterValues(prev.vendedor_id, lockedSellerFilter) ? prev : { ...prev, vendedor_id: lockedSellerFilter }
        ));
    }, [canEditSellerFilter, email, lockedSellerId]);

    useEffect(() => {
        setDraftFilters(filters);
    }, [filters]);

    useEffect(() => {
        if (!canEditSellerFilter && lockedSellerId === null) return;

        let ignore = false;

        const loadDashboard = async () => {
            setIsLoading(true);

            try {
                const nextData = await fetchCrmDashboard({ dateRange, filters });
                if (!ignore) setDashboardData(nextData);
            } catch (error) {
                if (!ignore) {
                    setMessage(error.message);
                    setStatusAlerta('error');
                    setOpenAlerta(true);
                }
            } finally {
                if (!ignore) setIsLoading(false);
            }
        };

        loadDashboard();
        return () => { ignore = true; };
    }, [canEditSellerFilter, dateRange, filters, lockedSellerId]);

    useEffect(() => {
        if (!canEditSellerFilter && lockedSellerId === null) return;

        let ignore = false;

        const loadPendingSignatures = async () => {
            setIsLoadingPending(true);

            try {
                const nextPendingSignatures = await fetchPendingSignatures(filters.vendedor_id);
                if (!ignore) setPendingSignatures(nextPendingSignatures);
            } catch (error) {
                if (!ignore) {
                    setPendingSignatures(EMPTY_PENDING_SIGNATURES);
                    setMessage(error.message);
                    setStatusAlerta('error');
                    setOpenAlerta(true);
                }
            } finally {
                if (!ignore) setIsLoadingPending(false);
            }
        };

        loadPendingSignatures();
        return () => { ignore = true; };
    }, [canEditSellerFilter, filters.vendedor_id, lockedSellerId]);

    useEffect(() => {
        if (isMobile) return;

        let ignore = false;

        const loadMarketingData = async () => {
            try {
                const nextMarketingData = await fetchMarketingData({ dateRange });
                if (!ignore) setMarketingData(nextMarketingData);
            } catch (error) {
                if (!ignore) {
                    setMessage(error.message);
                    setStatusAlerta('error');
                    setOpenAlerta(true);
                }
            }
        };

        loadMarketingData();
        return () => { ignore = true; };
    }, [dateRange, isMobile]);

    const handleMultiSelectDraftChange = (field, values) => {
        if (field === 'vendedor_id' && !canEditSellerFilter) return;
        setDraftFilters((prev) => ({ ...prev, [field]: normalizeMultiSelectValues(values) }));
    };

    const handleFilterClose = (field) => {
        if (field === 'vendedor_id' && !canEditSellerFilter) return;
        setFilters((prev) => (
            areSameFilterValues(prev[field], draftFilters[field]) ? prev : { ...prev, [field]: draftFilters[field] }
        ));
    };

    const handleDateRangeApply = (startDate, endDate) => {
        setDateRange({ startDate, endDate });
    };

    // O recorte é obrigatório na query: limpar volta ao período padrão em vez de esvaziar.
    const handleDateRangeReset = () => {
        setDateRange(buildDashboardDateRange());
    };

    if (isMobile) {
        return (
            <Box className='mainBox crm-dashboard-page crm-dashboard-mobile-page'>
                <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />
                <CrmDashboardMobile
                    canEditSellerFilter={canEditSellerFilter}
                    dateRange={dateRange}
                    draftFilters={draftFilters}
                    goal={goal}
                    isLoading={isLoading}
                    onDateRangeApply={handleDateRangeApply}
                    onDateRangeReset={handleDateRangeReset}
                    onFilterClose={handleFilterClose}
                    onMultiSelectDraftChange={handleMultiSelectDraftChange}
                    viewModel={viewModel}
                />
            </Box>
        );
    }

    return (
        <Box className='mainBox crm-dashboard-page'>
            <Alerta texto={message} status={statusAlerta} open={openAlerta} handleClose={() => setOpenAlerta(false)} />

            <div className='crm-dashboard-frame'>
                <div className='crm-dashboard-content'>
                    <div className='crm-dashboard-topbar'>
                        <DashboardHero />
                        <DashboardFilters
                            canEditSellerFilter={canEditSellerFilter}
                            dateRange={dateRange}
                            draftFilters={draftFilters}
                            onDateRangeApply={handleDateRangeApply}
                            onDateRangeReset={handleDateRangeReset}
                            onFilterClose={handleFilterClose}
                            onMultiSelectDraftChange={handleMultiSelectDraftChange}
                        />
                    </div>

                    {isLoading && <LinearProgress className='crm-dashboard-loading' variant='indeterminate' />}

                    <DashboardSummary
                        dateRange={dateRange}
                        goalTarget={goal.target}
                        marketingCost={marketingCost}
                        totalLeads={viewModel.totalLeads}
                        wonCount={viewModel.wonCount}
                        wonKwh={viewModel.wonKwh}
                    />

                    <DashboardPanels
                        funnelData={viewModel.funnelData}
                        originBreakdown={viewModel.originBreakdown}
                        sellerBreakdown={viewModel.sellerBreakdown}
                        statusBreakdown={viewModel.statusBreakdown}
                        totalLeads={viewModel.totalLeads}
                    >
                        <div className='crm-dashboard-goal-row'>
                            <SellerGoalCard goal={goal} isOwnGoal={!canEditSellerFilter} />
                            <PendingSignaturePanel
                                isLoading={isLoadingPending}
                                pendingSignatures={pendingSignatures}
                                sellerIds={filters.vendedor_id}
                            />
                        </div>
                    </DashboardPanels>
                </div>
            </div>
        </Box>
    );
};

export default CrmDashboard;
